import { gql } from '@apollo/client';
import { RUN_STATUS_FIELDS } from '@/graph/run-status';

export type { BackfillRunStatus, RunStatusState } from '@/graph/run-status';

// ============================================================================
// GraphQL Queries
// ============================================================================

export const GROUP_PERFORMANCE_SUMMARY_QUERY = gql`
  query GroupPerformanceSummary($period: AnalyticsPeriod!, $startDate: String, $endDate: String) {
    groupPerformanceSummary(period: $period, startDate: $startDate, endDate: $endDate) {
      hospitals {
        organizationId
        hospitalName
        fcots {
          percentage
          averageDelayMinutes
        }
        lpod {
          percentage
          averageDelayMinutes
        }
        turnover
        utilization
        cases
        rtor
      }
      meta {
        period
        source
        computedAt
      }
    }
  }
`;

export const EXECUTIVE_PERFORMANCE_SUMMARY_QUERY = gql`
  query ExecutivePerformanceSummary($period: AnalyticsPeriod!, $startDate: String, $endDate: String) {
    executivePerformanceSummary(period: $period, startDate: $startDate, endDate: $endDate) {
      hospitalName
      fcots {
        percentage
        averageDelayMinutes
      }
      lpod {
        percentage
        averageDelayMinutes
      }
      turnover
      utilization
      capacity
      cases
      rtor
      trend {
        date
        fcots {
          percentage
          averageDelayMinutes
        }
        lpod {
          percentage
          averageDelayMinutes
        }
        turnover
        utilization
        capacity
        cases
        rtor
      }
      previous {
        fcots {
          percentage
          averageDelayMinutes
        }
        lpod {
          percentage
          averageDelayMinutes
        }
        turnover
        utilization
        capacity
        cases
        rtor
      }
      meta {
        period
        source
        computedAt
      }
    }
  }
`;

export const EXECUTIVE_KPI_EARLIEST_DATE_QUERY = gql`
  query ExecutiveKpiEarliestDate {
    executiveKpiEarliestDate
  }
`;

export interface ExecutiveKpiEarliestDateData {
  executiveKpiEarliestDate: string | null;
}

// ============================================================================
// GraphQL Mutations
// ============================================================================

export const REFRESH_EXECUTIVE_KPI_CACHE = gql`
  mutation RefreshExecutiveKpiCache {
    refreshExecutiveKpiCache
  }
`;

export const INVOKE_ROLLUP_COORDINATOR = gql`
  mutation InvokeRollupCoordinator($dryRun: Boolean!) {
    invokeRollupCoordinator(dryRun: $dryRun) {
      success
      runId
      message
    }
  }
`;

export const SEND_EXECUTIVE_KPI_REPORT = gql`
  mutation SendExecutiveKpiReport($reportType: String!, $periodStart: String!, $recipientUserIds: [Long!]) {
    sendExecutiveKpiReport(reportType: $reportType, periodStart: $periodStart, recipientUserIds: $recipientUserIds) {
      success
      runId
      message
    }
  }
`;

export const EXECUTIVE_REPORT_SEND_STATUS = gql`
  query ExecutiveReportSendStatus($runId: String!) {
    executiveReportSendStatus(runId: $runId) {
      runId
      status
      startedAt
      completedAt
      durationMs
      totalRecipients
      successCount
      failedCount
      errorMessage
    }
  }
`;

export const INVOKE_ROLLUP_BACKFILL = gql`
  mutation InvokeRollupBackfill($backfillDays: Int!, $backfillMode: String!, $dryRun: Boolean!) {
    invokeRollupBackfill(backfillDays: $backfillDays, backfillMode: $backfillMode, dryRun: $dryRun) {
      success
      runId
      message
    }
  }
`;

export const BACKFILL_RUN_STATUS = gql`
  query BackfillRunStatus($runId: String!) {
    runStatus: backfillRunStatus(runId: $runId) {
      ...RunStatusFields
    }
  }
  ${RUN_STATUS_FIELDS}
`;

// ============================================================================
// TypeScript Types (matching GraphQL schema)
// ============================================================================

export enum AnalyticsPeriod {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  QUARTER = 'QUARTER',
  YEAR = 'YEAR',
  CUSTOM = 'CUSTOM',
}

export interface GroupPerformanceHospital {
  organizationId: string;
  hospitalName: string;
  fcots: ExecutiveDelayMetric;
  lpod: ExecutiveDelayMetric;
  turnover: number | null;
  utilization: number | null;
  cases: number;
  rtor: number | null;
}

export interface GroupPerformanceMeta {
  period: AnalyticsPeriod;
  source: 'live' | 'period_rollup' | 'daily_rollup_fallback';
  computedAt: string;
}

export interface GroupPerformanceSummary {
  hospitals: GroupPerformanceHospital[];
  meta: GroupPerformanceMeta;
}

export interface GroupPerformanceSummaryData {
  groupPerformanceSummary: GroupPerformanceSummary;
}

export interface GroupPerformanceSummaryVars {
  period: AnalyticsPeriod;
  startDate?: string;
  endDate?: string;
}

export interface ExecutiveKPIValues {
  fcots: ExecutiveDelayMetric;
  lpod: ExecutiveDelayMetric;
  turnover: number | null;
  utilization: number | null;
  capacity: number | null;
  cases: number;
  rtor: number | null;
}

export interface ExecutivePerformanceSummary {
  hospitalName: string;
  fcots: ExecutiveDelayMetric;
  lpod: ExecutiveDelayMetric;
  turnover: number | null;
  utilization: number | null;
  capacity: number | null;
  cases: number;
  rtor: number | null;
  trend: ExecutiveKpiTrendPoint[];
  previous: ExecutiveKPIValues | null;
  meta: GroupPerformanceMeta;
}

export interface ExecutiveKpiTrendPoint {
  date: string;
  fcots: ExecutiveDelayMetric;
  lpod: ExecutiveDelayMetric;
  turnover: number | null;
  utilization: number | null;
  capacity: number | null;
  cases: number;
  rtor: number | null;
}

export interface ExecutiveDelayMetric {
  percentage: number | null;
  averageDelayMinutes: number | null;
}

export interface ExecutivePerformanceSummaryData {
  executivePerformanceSummary: ExecutivePerformanceSummary;
}

export interface ExecutivePerformanceSummaryVars {
  period: AnalyticsPeriod;
  startDate?: string;
  endDate?: string;
}

export interface LambdaInvokeResult {
  success: boolean;
  message: string;
}

export interface InvokeRollupCoordinatorData {
  invokeRollupCoordinator: BackfillInvokeResult;
}

export interface InvokeRollupCoordinatorVars {
  dryRun: boolean;
}

export interface BackfillInvokeResult {
  success: boolean;
  runId: string | null;
  message: string;
}

export interface InvokeRollupBackfillData {
  invokeRollupBackfill: BackfillInvokeResult;
}

export interface InvokeRollupBackfillVars {
  backfillDays: number;
  backfillMode: 'missing_only' | 'overwrite_all';
  dryRun: boolean;
}

export interface SendExecutiveKpiReportData {
  sendExecutiveKpiReport: BackfillInvokeResult;
}

export interface SendExecutiveKpiReportVars {
  reportType: string;
  periodStart: string;
  recipientUserIds?: number[];
}

export interface ReportSendStatus {
  runId: string;
  status: 'running' | 'success' | 'partial_success' | 'failed';
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  totalRecipients: number;
  successCount: number;
  failedCount: number;
  errorMessage: string | null;
}

export interface ExecutiveReportSendStatusData {
  executiveReportSendStatus: ReportSendStatus | null;
}

export interface ExecutiveReportSendStatusVars {
  runId: string;
}

export const SEND_SCHEDULED_ANALYTICS_REPORT = gql`
  mutation SendScheduledAnalyticsReport($reportType: String!) {
    sendScheduledAnalyticsReport(reportType: $reportType) {
      success
      runId
      message
    }
  }
`;

export interface SendScheduledAnalyticsReportData {
  sendScheduledAnalyticsReport: BackfillInvokeResult;
}

export interface SendScheduledAnalyticsReportVars {
  reportType: 'weekly' | 'monthly';
}

export const SEND_OR_SCHEDULE_REPORT = gql`
  mutation SendOrScheduleReport($reportDate: String!, $recipientUserIds: [Long!]) {
    sendOrScheduleReport(reportDate: $reportDate, recipientUserIds: $recipientUserIds) {
      success
      runId
      message
    }
  }
`;

export const OR_SCHEDULE_REPORT_SEND_STATUS = gql`
  query OrScheduleReportSendStatus($runId: String!) {
    orScheduleReportSendStatus(runId: $runId) {
      runId
      status
      startedAt
      completedAt
      durationMs
      totalRecipients
      successCount
      failedCount
      errorMessage
    }
  }
`;

export interface SendOrScheduleReportData {
  sendOrScheduleReport: BackfillInvokeResult;
}

export interface SendOrScheduleReportVars {
  reportDate: string;
  recipientUserIds?: number[];
}

export interface OrScheduleReportSendStatusData {
  orScheduleReportSendStatus: ReportSendStatus | null;
}

export interface OrScheduleReportSendStatusVars {
  runId: string;
}

// ============================================================================
// Helper: Map UI period strings to API enum
// ============================================================================

export type TimePeriodKey = 'd' | 'w' | 'm' | 'q' | 'y' | 'c';

export const periodMapping: Record<TimePeriodKey, AnalyticsPeriod> = {
  d: AnalyticsPeriod.DAY,
  w: AnalyticsPeriod.WEEK,
  m: AnalyticsPeriod.MONTH,
  q: AnalyticsPeriod.QUARTER,
  y: AnalyticsPeriod.YEAR,
  c: AnalyticsPeriod.CUSTOM,
};

/**
 * Convert a UI time period key to the GraphQL AnalyticsPeriod enum.
 * Defaults to MONTH if the period is not recognized.
 */
export const toAnalyticsPeriod = (period: string): AnalyticsPeriod =>
  periodMapping[period as TimePeriodKey] ?? AnalyticsPeriod.MONTH;
